import React, { useState, useEffect, useMemo } from "react";
import api from "./services/api";
import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";
import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { ActiveMenuProvider } from "./context/ActiveMenuContext";
import Favicon from "react-favicon";
import { getBackendUrl, DEFAULT_APP_NAME, resolveAppName } from "./config";
import Routes from "./routes";
import defaultLogoLight from "./assets/logo.png";
import defaultLogoDark from "./assets/logo-black.png";
import defaultLogoFavicon from "./assets/favicon.ico";
import useSettings from "./hooks/useSettings";
import { setupNativeAppBridge } from "./utils/nativeAppBridge";

const queryClient = new QueryClient();

const App = () => {
  const [locale, setLocale] = useState();
  const appColorLocalStorage = localStorage.getItem("primaryColorLight") || localStorage.getItem("primaryColorDark") || "#065183";
  const appNameLocalStorage = resolveAppName(localStorage.getItem("appName"));
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const preferredTheme = window.localStorage.getItem("preferredTheme");
  const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");
  const [primaryColorLight, setPrimaryColorLight] = useState(appColorLocalStorage);
  const [primaryColorDark, setPrimaryColorDark] = useState(appColorLocalStorage);
  const [appLogoLight, setAppLogoLight] = useState(defaultLogoLight);
  const [appLogoDark, setAppLogoDark] = useState(defaultLogoDark);
  const [appLogoFavicon, setAppLogoFavicon] = useState(defaultLogoFavicon);
  const [appName, setAppName] = useState(appNameLocalStorage);
  const { getPublicSetting } = useSettings();
  // Estado para controlar o prompt de instalação do PWA
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === "light" ? "dark" : "light";
          window.localStorage.setItem("preferredTheme", newMode); // Persistindo o tema no localStorage
          return newMode;
        });
      },
      setPrimaryColorLight,
      setPrimaryColorDark,
      setAppLogoLight,
      setAppLogoDark,
      setAppLogoFavicon,
      setAppName,
      appLogoLight,
      appLogoDark,
      appLogoFavicon,
      appName,
      mode,
    }),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, mode]
  );

  const theme = useMemo(
    () =>
      createTheme(
        {
          scrollbarStyles: {
            "&::-webkit-scrollbar": {
              width: "8px",
              height: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
              backgroundColor: mode === "light" ? primaryColorLight : primaryColorDark,
            },
          },
          scrollbarStylesSoft: {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
            },
          },
          shape: {
            borderRadius: 12,
          },
          typography: {
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            h5: { fontWeight: 600, letterSpacing: "-0.02em" },
            h6: { fontWeight: 600, letterSpacing: "-0.01em" },
            subtitle1: { fontWeight: 500 },
            button: { fontWeight: 500, letterSpacing: "0.01em" },
          },
          overrides: {
            MuiCssBaseline: {
              "@global": {
                html: {
                  overflowX: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                  position: "relative",
                  overscrollBehaviorX: "none",
                  backgroundColor: mode === "light" ? "#f1f5f9" : "#0f172a",
                  minHeight: "100%",
                },
                body: {
                  overflowX: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                  position: "relative",
                  overscrollBehaviorX: "none",
                  backgroundColor: mode === "light" ? "#f1f5f9" : "#0f172a",
                  minHeight: "100%",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                },
                "#root": {
                  overflowX: "hidden",
                  width: "100%",
                  maxWidth: "100%",
                  position: "relative",
                  overscrollBehaviorX: "none",
                  backgroundColor: mode === "light" ? "#f1f5f9" : "#0f172a",
                  minHeight: "100%",
                },
                ".layout-container, .main-content, .app-wrapper": {
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  overscrollBehaviorX: "none",
                },
                ".kanban-wrapper, .tickets-wrapper": {
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                  overscrollBehaviorX: "none",
                },
                ".kanban-wrapper .react-trello-board": {
                  height: "100% !important",
                  maxHeight: "100% !important",
                  minHeight: "0 !important",
                  overflow: "hidden !important",
                  flexDirection: "row !important",
                  backgroundColor: "transparent !important",
                  padding: "0 !important",
                },
                ".kanban-wrapper .react-trello-lane > div": {
                  justifyContent: "flex-start !important",
                  alignItems: "stretch !important",
                  alignSelf: "stretch !important",
                  minWidth: "0 !important",
                  marginTop: "0 !important",
                },
                ".kanban-wrapper .react-trello-lane .smooth-dnd-container.vertical": {
                  justifyContent: "flex-start !important",
                  alignItems: "center !important",
                },
                ".kanban-wrapper .react-trello-lane article": {
                  width: "288px !important",
                  maxWidth: "288px !important",
                  minWidth: "0 !important",
                  margin: "0 auto !important",
                  overflow: "visible !important",
                  boxSizing: "border-box !important",
                },
                ".kanban-wrapper .react-trello-lane article header > span:first-child": {
                  width: "auto !important",
                  maxWidth: "55% !important",
                  minWidth: "0 !important",
                },
                ".kanban-wrapper .react-trello-lane article header > span:nth-child(2)": {
                  width: "auto !important",
                  minWidth: "88px !important",
                  maxWidth: "42% !important",
                  paddingRight: "2px !important",
                },
                "@media (max-width: 768px)": {
                  html: {
                    overflowX: "hidden !important",
                  },
                  body: {
                    overflowX: "hidden !important",
                    touchAction: "pan-y pinch-zoom",
                    backgroundColor: mode === "light" ? "#f1f5f9 !important" : "#0f172a !important",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                  },
                  "#root": {
                    overflowX: "hidden !important",
                    maxWidth: "100% !important",
                    backgroundColor: mode === "light" ? "#f1f5f9" : "#0f172a",
                    minHeight: "100%",
                  },
                  ".layout-container, .main-content": {
                    maxWidth: "100% !important",
                    overflowX: "hidden !important",
                  },
                  ".kanban-wrapper, .tickets-wrapper": {
                    width: "100% !important",
                    maxWidth: "100% !important",
                    overflowX: "hidden !important",
                  },
                  ".kanban-scroll-container": {
                    touchAction: "pan-x",
                    overscrollBehaviorX: "contain",
                  },
                },
              },
            },
            MuiButton: {
              root: {
                borderRadius: 10,
                textTransform: "none",
                fontWeight: 500,
                padding: "8px 20px",
              },
              contained: {
                boxShadow: mode === "light"
                  ? "0 1px 2px rgba(15,23,42,0.08)"
                  : "0 2px 8px rgba(0,0,0,0.3)",
                "&:hover": {
                  boxShadow: mode === "light"
                    ? "0 4px 12px rgba(15,23,42,0.12)"
                    : "0 4px 16px rgba(0,0,0,0.4)",
                },
              },
              outlined: {
                borderColor: mode === "light" ? "rgba(15,23,42,0.12)" : "rgba(148,163,184,0.24)",
              },
            },
            MuiIconButton: {
              root: {
                borderRadius: 10,
              },
            },
            MuiPaper: {
              rounded: {
                borderRadius: 12,
              },
              elevation1: {
                boxShadow: mode === "light"
                  ? "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)"
                  : "0 2px 8px rgba(0,0,0,0.28)",
              },
              elevation2: {
                boxShadow: mode === "light"
                  ? "0 4px 12px rgba(15,23,42,0.08)"
                  : "0 4px 16px rgba(0,0,0,0.32)",
              },
            },
            MuiTextField: {
              root: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: 10,
                },
              },
            },
            MuiTabs: {
              indicator: {
                height: 3,
                borderRadius: 3,
              },
            },
            MuiTab: {
              root: {
                textTransform: "none",
                fontWeight: 500,
                minHeight: 44,
              },
            },
            MuiTableCell: {
              head: {
                fontWeight: 600,
                fontSize: "0.8125rem",
                color: mode === "light" ? "#64748b" : "#94a3b8",
                borderBottom: mode === "light"
                  ? "1px solid rgba(15,23,42,0.08)"
                  : "1px solid rgba(148,163,184,0.12)",
              },
            },
            MuiDrawer: {
              paper: {
                borderRight: mode === "light"
                  ? "1px solid rgba(15,23,42,0.08)"
                  : "1px solid rgba(148,163,184,0.1)",
              },
            },
          },
          palette: {
            type: mode,
            primary: { main: mode === "light" ? primaryColorLight : primaryColorDark },
            secondary: { main: mode === "light" ? "#64748b" : "#94a3b8" },
            background: {
              default: mode === "light" ? "#f1f5f9" : "#0f172a",
              paper: mode === "light" ? "#ffffff" : "#1e293b",
            },
            text: {
              primary: mode === "light" ? "#0f172a" : "#f1f5f9",
              secondary: mode === "light" ? "#64748b" : "#94a3b8",
            },
            divider: mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(148,163,184,0.12)",
            surface: { main: mode === "light" ? "#ffffff" : "#1e293b" },
            surfaceMuted: { main: mode === "light" ? "#f8fafc" : "#172033" },
            textPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            borderPrimary: mode === "light" ? primaryColorLight : primaryColorDark,
            dark: { main: mode === "light" ? "#0f172a" : "#f1f5f9" },
            light: { main: mode === "light" ? "#f8fafc" : "#334155" },
            fontColor: mode === "light" ? primaryColorLight : primaryColorDark,
            tabHeaderBackground: mode === "light" ? "#ffffff" : "#1e293b",
            optionsBackground: mode === "light" ? "#f8fafc" : "#172033",
            fancyBackground: mode === "light" ? "#f1f5f9" : "#0f172a",
            total: mode === "light" ? "#f1f5f9" : "#0f172a",
            messageIcons: mode === "light" ? "#64748b" : "#94a3b8",
            inputBackground: mode === "light" ? "#ffffff" : "#1e293b",
            barraSuperior: mode === "light" ? primaryColorLight : "#1e293b",
          },
          mode,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName,
          calculatedLogoDark: () => {
            if (appLogoDark === defaultLogoDark && appLogoLight !== defaultLogoLight) {
              return appLogoLight;
            }
            return appLogoDark;
          },
          calculatedLogoLight: () => {
            if (appLogoDark !== defaultLogoDark && appLogoLight === defaultLogoLight) {
              return appLogoDark;
            }
            return appLogoLight;
          },
        },
        locale
      ),
    [appLogoLight, appLogoDark, appLogoFavicon, appName, locale, mode, primaryColorDark, primaryColorLight]
  );

  useEffect(() => {
    setupNativeAppBridge();
  }, []);

  useEffect(() => {
    const resolvedName = resolveAppName(appName);
    localStorage.setItem("appName", resolvedName);
    const splashBrand = document.getElementById("splash-brand");
    if (splashBrand) {
      splashBrand.textContent = resolvedName;
    }
  }, [appName]);

  // Detecta quando o navegador está pronto para mostrar o prompt de instalação do PWA
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Previne o comportamento padrão do navegador
      e.preventDefault();
      // Armazena o evento para uso posterior
      setDeferredPrompt(e);
      
      // Mostra o prompt de instalação imediatamente
      setTimeout(() => {
        showInstallPrompt();
      }, 2000); // Pequeno delay para garantir que a página já carregou
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Função para mostrar o prompt de instalação
  const showInstallPrompt = () => {
    if (deferredPrompt) {
      // Verifica se o PWA já está instalado
      if (!window.matchMedia('(display-mode: standalone)').matches) {
        // Mostra o prompt de instalação
        deferredPrompt.prompt();
        
        // Espera pela resposta do usuário
        deferredPrompt.userChoice.then((choiceResult) => {
          if (choiceResult.outcome === 'accepted') {
            console.log('Usuário aceitou instalar o app');
          } else {
            console.log('Usuário recusou instalar o app');
          }
          // Limpa o prompt armazenado, só pode ser usado uma vez
          setDeferredPrompt(null);
        });
      }
    }
  };

  useEffect(() => {
    // Forzar idioma español por defecto
    const i18nlocale = localStorage.getItem("i18nextLng");
    if (!i18nlocale || i18nlocale.startsWith("pt")) {
      localStorage.setItem("i18nextLng", "es");
    }
    
    const browserLocale = i18nlocale ? i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5) : "es";

    if (browserLocale === "ptBR") {
      setLocale(ptBR);
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("preferredTheme", mode);
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.style.backgroundColor = mode === "light" ? "#f1f5f9" : "#0f172a";
    document.body.style.backgroundColor = mode === "light" ? "#f1f5f9" : "#0f172a";
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute("content", mode === "light" ? primaryColorLight : "#0f172a");
    }
  }, [mode, primaryColorLight]);

  useEffect(() => {
    console.log("|=========== handleSaveSetting ==========|")
    console.log("APP START")
    console.log("|========================================|")
   
    // Cargar configuraciones en paralelo para mejorar el rendimiento
    const loadSettings = async () => {
      try {
        const [
          primaryColorLight,
          primaryColorDark,
          appLogoLight,
          appLogoDark,
          appLogoFavicon,
          appName
        ] = await Promise.allSettled([
          getPublicSetting("primaryColorLight"),
          getPublicSetting("primaryColorDark"),
          getPublicSetting("appLogoLight"),
          getPublicSetting("appLogoDark"),
          getPublicSetting("appLogoFavicon"),
          getPublicSetting("appName")
        ]);

        setPrimaryColorLight(primaryColorLight.value || "#0000FF");
        setPrimaryColorDark(primaryColorDark.value || "#39ACE7");
        setAppLogoLight(appLogoLight.value ? getBackendUrl() + "/public/" + appLogoLight.value : defaultLogoLight);
        setAppLogoDark(appLogoDark.value ? getBackendUrl() + "/public/" + appLogoDark.value : defaultLogoDark);
        setAppLogoFavicon(appLogoFavicon.value ? getBackendUrl() + "/public/" + appLogoFavicon.value : defaultLogoFavicon);
        setAppName(resolveAppName(appName.value));
      } catch (error) {
        console.log("Error loading settings:", error);
        // Valores por defecto en caso de error
        setPrimaryColorLight("#0000FF");
        setPrimaryColorDark("#39ACE7");
        setAppLogoLight(defaultLogoLight);
        setAppLogoDark(defaultLogoDark);
        setAppLogoFavicon(defaultLogoFavicon);
        setAppName(DEFAULT_APP_NAME);
      }
    };

    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--primaryColor", mode === "light" ? primaryColorLight : primaryColorDark);
  }, [primaryColorLight, primaryColorDark, mode]);

  useEffect(() => {
    async function fetchVersionData() {
      try {
        const response = await api.get("/version");
        const { data } = response;
        window.localStorage.setItem("frontendVersion", data.version);
      } catch (error) {
        console.log("Error fetching data", error);
      }
    }
    fetchVersionData();
  }, []);

  return (
    <>
      <Favicon url={appLogoFavicon || defaultLogoFavicon} />
      <ColorModeContext.Provider value={{ colorMode }}>
        <ThemeProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <ActiveMenuProvider>
  <div style={{ position: "relative", overflow: "visible", zIndex: 0, minHeight: "100dvh", backgroundColor: mode === "light" ? "#f1f5f9" : "#0f172a" }}>
    <Routes />
  </div>
            </ActiveMenuProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </>
  );
};

export default App;